import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useState } from 'react'

import LocationNotesAccordionVariant from 'components/notes/location/LocationNotesAccordionVariant'
import InteractiveTagsList from 'components/tags/InteractiveTagsList'
import { selectAddressName, selectLatitude, selectLongitude } from 'data/address/addressSelectors'
import { MAP_DEFAULT_OPTIONS } from 'settings/constants/map'
import T from 'T'
import { get } from 'utils/lodash'

import ContactsListing from '../contacts/ContactsListing'

import LocationContent from './LocationContent'
import LocationMapImage from './LocationMapImage'

const { SATELLITE_STREET_VIEW } = MAP_DEFAULT_OPTIONS

const LEFT_SECTION_WIDTH = 340
const IMAGE_WIDTH_HORIZONTAL = 280
const IMAGE_WIDTH_VERTICAL = 399
const IMAGE_HEIGHT = 250
const HORIZONTAL = 'horizontal' as const
const VERTICAL = 'vertical' as const

const LocationNotesAccordionVariantUntyped = LocationNotesAccordionVariant as React.ElementType
const ContactsListingUntyped = ContactsListing as React.ElementType
const LocationMapImageUntyped = LocationMapImage as React.ElementType

type SelectedLocation = Record<string, unknown>

type LocationExpandViewProps = {
  caller: string
  selectedLocation: SelectedLocation
  orientation: typeof HORIZONTAL | typeof VERTICAL
  onSaveTags: () => void
  accountId: string
  dynamicWidth?: boolean
  showNotes?: boolean
  showContacts?: boolean
  showParcelNumber?: boolean
  showSatelliteView?: boolean
  showLocationContent?: boolean
}

const LocationExpandView: React.FC<LocationExpandViewProps> = ({
  caller,
  selectedLocation,
  orientation,
  onSaveTags,
  accountId,
  dynamicWidth = false,
  showNotes = false,
  showContacts = true,
  showParcelNumber = false,
  showSatelliteView = false,
  showLocationContent = true,
}) => {
  const [contactHeight, setContactHeight] = useState(IMAGE_HEIGHT)
  const selectedLocationId = get(selectedLocation, 'id')
  const locationAddress = get(selectedLocation, 'address', {})
  const contacts = get(selectedLocation, 'contacts', [])
  const tags = get(selectedLocation, 'tags', [])
  const addressName = selectAddressName(locationAddress)
  const longitude = selectLongitude(locationAddress)
  const latitude = selectLatitude(locationAddress)
  const locationContentHeight = document.querySelector('.location-content')?.clientHeight
  const isVertical = orientation === VERTICAL
  const isHorizontal = orientation === HORIZONTAL

  useEffect(() => {
    if (locationContentHeight) {
      setContactHeight(locationContentHeight)
    }
  }, [locationContentHeight])

  const handleSaveTags = useCallback(
    (_data: unknown, closeCallback: () => void) => {
      closeCallback()
      onSaveTags()
    },
    [onSaveTags]
  )

  return (
    <Grid container direction={isVertical ? 'column' : 'row'} flexWrap="nowrap">
      <Grid
        container
        columnSpacing={isVertical ? 0 : 2}
        direction={isVertical ? 'column' : 'row'}
        flexWrap="nowrap"
        rowSpacing={isVertical ? 2 : 0}
      >
        <Grid size="auto">
          <LocationMapImageUntyped
            containerId={`location-${selectedLocationId}-map-preview-${caller}`}
            dynamicWidth={dynamicWidth}
            height={IMAGE_HEIGHT}
            latitude={latitude}
            longitude={longitude}
            mapStyle={showSatelliteView ? SATELLITE_STREET_VIEW : undefined}
            width={isVertical ? IMAGE_WIDTH_VERTICAL : IMAGE_WIDTH_HORIZONTAL}
          />
        </Grid>

        <Grid size="grow">
          {showLocationContent && (
            <Box px={2}>
              <LocationContent
                showAddressName
                address={locationAddress}
                addressName={addressName}
                parcelNumber={get(selectedLocation, 'parcelNumber', '')}
                showParcelNumber={showParcelNumber}
              />
            </Box>
          )}
          <Box mt={1.5} px={2}>
            <InteractiveTagsList
              disableAccount
              disableService
              accountId={accountId}
              data={{
                locationId: selectedLocationId,
                name: addressName,
                tagDetails: { forLocation: tags },
              }}
              onSave={handleSaveTags}
            />
          </Box>
        </Grid>
      </Grid>
      {showContacts && (
        <>
          {isVertical && <Divider flexItem orientation="horizontal" sx={{ my: 1 }} />}
          {isHorizontal && <Divider flexItem orientation="vertical" />}
          <Grid size="auto">
            <Box alignItems="center" display="flex" justifyContent="space-between" px={2}>
              <Typography color="textSecondary" fontWeight={500} variant="h5">
                Location contacts
              </Typography>
            </Box>
            <Box
              sx={
                isHorizontal
                  ? {
                      overflow: 'auto',
                      width: `${LEFT_SECTION_WIDTH}px`,
                      maxHeight: `${Number(contactHeight) > IMAGE_HEIGHT ? contactHeight : IMAGE_HEIGHT}px`,
                    }
                  : undefined
              }
            >
              <ContactsListingUntyped contacts={contacts} noContactMessage={T.NO_LOCATION_CONTACTS} />
            </Box>
          </Grid>
        </>
      )}
      {showNotes && (
        <>
          <Divider flexItem orientation="horizontal" sx={{ mt: 1 }} />
          <LocationNotesAccordionVariantUntyped locationId={selectedLocationId} />
        </>
      )}
    </Grid>
  )
}

export default LocationExpandView
